import { FC, useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";

import { Modal, useAlert } from "capitalrx-components-2.0/lib/general";
import { Shelf } from "capitalrx-components-2.0/lib/layout";
import { Paragraph } from "capitalrx-components-2.0/lib/typography";

import { resolveSSO } from "@/App/api/sso";
import { ThemedButton } from "@/App/components";
import { StorageKeys } from "@/App/constants/constants";
import { AuthenticationContext } from "@/App/context";
import { useNavigateClient } from "@/App/hooks";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service/analyticsService";

export const SSOCallbackPage: FC = () => {
  const { t: translate } = useTranslation();
  const [searchParams] = useSearchParams();
  const resolveId = searchParams.get("resolve_id");
  const navigate = useNavigateClient();
  const alert = useAlert();
  const [modalVisible, setModalVisible] = useState(false);
  const { setAuthState, setStsTokens, logoutUser } = useContext(AuthenticationContext);

  useEffect(() => {
    logoutUser();

    if (resolveId) {
      resolveSSO(resolveId)
        .then((response) => {
          setStsTokens({
            accessToken: response.access_token,
            refreshToken: response.refresh_token,
          });
          localStorage.setItem(StorageKeys.ATTRIBUTES, JSON.stringify(response.attributes || {}));

          logEvent(ANALYTIC_EVENTS.LOGIN.ssoSuccess);

          const targetUrl = response.target_url || "/";

          if (response.offer_native_registration){
            sessionStorage.setItem("targetUrl", targetUrl);
            setModalVisible(true);
          } else {
            if(!targetUrl.includes("/sso/register")) {
              setAuthState("signedIn");
            }
            navigate(targetUrl);
          }
        })
        .catch(() => {
          setAuthState("signedOut");
          logEvent(ANALYTIC_EVENTS.LOGIN.ssoFailure);
          alert({
            message: translate("SSO_CALLBACK_PAGE.errorResolvingSso"),
            type: "error",
            style: "toast",
          });
          navigate("/login");
        });
    } else {
      setAuthState("signedOut");
      navigate("/login");
    }
  }, []);

  const setNativeRegistrationFlow = (approved: boolean) => {
    setModalVisible(false);
    setAuthState("signedIn");
    const target_url = sessionStorage.getItem("targetUrl");
    if(approved){
      navigate("/add-native-authentication");
    } else {
      sessionStorage.removeItem("targetUrl");
      navigate(target_url);
    }
  };

  return (
    <div>
      <Paragraph testId="resolving-sso-text" textAlign="center">{translate("SSO_CALLBACK_PAGE.resolvingSso")}</Paragraph>
      <Modal
        testId="register-native-confirmation"
        title={translate("SSO_CALLBACK_PAGE.nativeRegistration")}
        footer={
          <Shelf>
            <Shelf.Spacer />
            <ThemedButton appearance="secondary" onClick={() => setNativeRegistrationFlow(false)}>
              {translate("SSO_CALLBACK_PAGE.no")}
            </ThemedButton>
            <ThemedButton appearance="primary" onClick={() => setNativeRegistrationFlow(true)}>
              {translate("SSO_CALLBACK_PAGE.yes")}
            </ThemedButton>
          </Shelf>
        }
        visible={modalVisible}
        onClose={() => setNativeRegistrationFlow(false)}
      >
        {translate("SSO_CALLBACK_PAGE.setupNativeLogin")}
      </Modal>
    </div>
  );
};
